export default {
  title: 'New EKRM',
  createReferenceModel: 'Create Reference Model',
  introduction: 'Introduction',
  considerationsTitle: 'Considerations While Completing This Template',
  reference: 'Reference',
  mediaSpend: 'Media Spend',
  decay: 'Decay',
  saturation: 'Saturation',
  kpi: 'KPI',
  seasonalityAndSpotEvents: 'Seasonality & Spot Events',
  attributions: 'Attributions',
  futureTrends: 'Future Trends',
  nextStep: 'Next Step',
  previousStep: 'Previous Step',
  categoryName: 'Category Name',
  categoryNameInfo: 'This is the name by which the generated EKRM will be recognized (eg. Automotive).',
  market: 'Market',
  marketInfo: 'Select the target market for this EKRM.',
  ownControllableMedia: 'Own Controllable Media',
  ownControllableMediaInfo: 'Select Own Controllable Media to use for the EKRM. You can add Custom Media.',
  addCustomMedia: 'Add Custom Media',
  customMediaName: 'Custom Media Name',
  customMediaValidation: 'Only letters, numbers, and spaces allowed.',
  cancel: 'Cancel',
  add: 'Add',
  selectMediaToContinue: 'Select Own Controllable Media to continue',
  variationRange: 'Variation Range',
  variationRangeInfo: 'Indication of how much the typical spend reported can actually vary within the category.',
  ownNonControllableMedia: 'Own Non-Controllable Media/Co-Marketing',
  competition: 'Competition',
  maximumEffect: 'Maximum Effect',
  halfLife: 'Half Life',
  maximumSpend: 'Maximum Spend',
  saturationPoint: 'Saturation Point',
  revenueVariationRange: 'Revenue Variation Range',
  revenue: 'Revenue',
  spend: 'Spend',
  avgMonthlyRevenue: 'Avg. Monthly Revenue (USD)',
  percentSalesAttributedPeriodic: '% of Sales Attributed to Periodic/Cyclic Events',
  percentSalesAttributedSpot: '% of Sales Attributed to Spot Events',
  percentSalesAttributedByComponent: '% of Sales Attributed by Component',
  expectedGrowthRate: 'Expected Growth Rate For The Following 12 Months',
  errorsAndWarnings: 'Found some errors (must fix) and warnings (should fix for good model quality):',
  goBackAndFix: 'Go back and fix them',
  ignoreWarnings: 'Ignore warnings and create the model anyway',
  january: 'January',
  february: 'February',
  march: 'March',
  april: 'April',
  may: 'May',
  june: 'June',
  july: 'July',
  august: 'August',
  september: 'September',
  october: 'October',
  november: 'November',
  december: 'December',
  // Introduction section
  introP1: 'Everything in this template is required since these are the minimum parameters needed to build a proper model. With that said, and the reason why these are called "Expert Knowledge Models", is because we need the expert\'s know-how about the category-market in order to provide the best-guess about these parameters because their best-guess will be better than anyone else\'s.',
  introP2: 'Given the above, these parameters do not (necessarily) need to come or match a specific study, rather they are the result of the expert\'s experience and know-how gained after many studies. In a way, these parameters represent an "average" across all existing models for that category-market combination. Also, this is the reason why in most cases we include a +/- variation, precisely to indicate the level of uncertainty that the expert has about that specific parameter (or the actual variance of the category-market).',
  introP3: 'Another way of thinking about it is that, in order to build a model that will produce reasonable results, all the parameters we are asking for are needed. We could try to take a guess on some of them, but obviously our guess will be significantly worse than that of someone who actually knows the category-market inside out, so even if some of those parameters are not available through an already existing study/model, the expert\'s estimate will still be better than anyone else\'s.',
  introP4Bold: 'So the most important message is: do not to stress over trying to get these parameters perfect, they\'ll never be, they just need to be close enough to reality in order to produce a good reference.',
  introP4: 'Keep in mind that the fine-tuning will happen when during the customization process, where the reference will be used to produce a brand-specific model by precisely customizing the reference to that brand.',
  // Media Spend section
  ownNonControllableMediaInfo: 'This accounts for any spend performed by the brand but that\'s not directly related to the category being reported. The total spend is required, no by-channel breakdown is needed. For example, this can be brand-level advertising and/or other related products outside of the category.',
  competitionInfo: 'When available, this will represent the total media spend from direct/relevant competitors in the category. The selection of those competitors will vary across markets and categories, so it\'s up to the expert to make that selection.',
  // Decay section
  minMaxRange: 'Min/Max Range',
  minMaxRangeInfo: 'How the different parameters can vary across the category.',
  maximumEffectInfo: 'Number of months until a spending "pulse" reaches it\'s maximum effect. Typically, for digital channels, this number tends to be zero or very close to zero, whereas more traditional channels like TV tend to have a short wear-in period.',
  halfLifeInfo: 'Time, in months, it takes for the impact of a spending pulse to reach 50%.',
  halfLifeAutoNote: 'Half Life allowed ranges will vary automatically when you set the range for Maximum Effect.',
  // Saturation section
  saturationCurvesInfo: 'These curves represent the response, in revenue, for every dollar spent by media channel. If revenue in USD is not available, please make the necessary conversions in order to transform the units to USD (e.g., multiply by an average ticket per unit sold).',
  maximumSpendInfo: 'Automatically calculated from the data entered in Media Spend.',
  saturationPointInfo: 'Point where the revenue increase, when spending an extra dollar, is marginal. This value is used to automatically generate the saturation curve\'s spend values (10-column yellow row).',
  revenueVariationRangeInfo: 'Is meant to create a variation band around the actual response curve, in order to account for variations within the category.',
  tenColumnRowLabel: '10-column white row',
  tenColumnRowInfo: 'Revenue generated when media spending the amount in the yellow cell above.',
  // KPI section
  kpiRevenueNote: 'If revenue in USD is not available, please make the necessary conversions in order to transform the units to USD (e.g., multiply by an average ticket per unit sold).',
  // Seasonality section
  seasonalityInfo: 'These values are rough estimates of the portion of sales that can be attributed to periodic/cyclic events (ie. those that repeat in a seasonal pattern year over year) and those "spot events" that take place throughout the year (ie. those that only happen once a year, but do repeat from one year to the next).',
  seasonalityValuesNote: 'Values can be either positive or negative, indicating whether the contribution is positive or negative to sales.',
  // Attributions section
  attributionsYellowCells: 'The values for Periodic/Cyclic, Spot Events, and Base (yellow cells) are automatically calculated from the data entered in this and other sections of the form.',
  mediaSpendContributions: 'Media Spend (own, non-controllable and competition) contributions refer to the percentage of sales that each one of these components represent.',
  minMaxDifference: 'The difference between min and max values for any given component must be larger than 3.',
  nonControllableNote: 'Non-Controllable and Competition ranges should only be filled if data was entered for them in the Media Spend section.',
  totalSumNote: 'Total sum of the Min column (not considering Base) must be less than 100.',
  competitionSpendNote: 'Competition Spend attribution cannot be positive.',
  // Future Trends section
  futureTrendsInfo: 'These numbers only represent future expectations about growth towards the future, and will be used when propagating model values for simulations and optimizations in a future period of time. These are general expectations which can be overidden in the plan creation app anyway, so there\'s no need to stress about getting them perfect.',
  // Spreadsheet column headers
  min: 'Min',
  max: 'Max',
  media: 'Media',
  other: 'Other',
  variationRangePercent: 'Variation Range (+/- %)',
  // Spreadsheet row names
  salesRevenue: 'Sales (Revenue)',
  ownControllableMediaSpendTotal: 'Own Controllable Media Spend (Total)',
  ownNonControllableMediaSpendTotal: 'Own Non-Controllable Media/Co-Marketing Spend (Total)',
  competitionSpendTotal: 'Competition Spend (Total)',
  periodicCyclicEvents: 'Periodic/Cyclic Events',
  spotEvents: 'Spot Events',
  base: 'Base',
  ownNonControllableMediaTotal: 'Own Non-Controllable Media/Co-Marketing (Total)',
  competitionTotal: 'Competition (Total)'
}
